import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Button, Card, Col, Form, Input, Row, App, Select, Space, 
  Upload, Switch, Collapse, Badge, Typography, Divider, Modal 
} from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';

// Servicios
import { 
  getExerciseById, 
  createExercise, 
  updateExercise, 
  checkTitleExists,
  getExerciseAlternatives,
  listExercisesVariants,
  uploadExerciseAudio,
  uploadExerciseMedia,
} from '../../services/exercises';
import { listExerciseTypes, listCatalog } from '../../services/catalogs';
import { resolveMedia } from '../../utils/media';

const { useApp } = App;
const { Text, Title } = Typography;
const { Panel } = Collapse;

const ExerciseUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  
  // Catálogos
  const [exerciseVariantsCatalog, setExerciseVariantsCatalog] = useState([]);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [muscles, setMuscles] = useState([]);
  const [trainingStyles, setTrainingStyles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [executors, setExecutors] = useState([]);

  // Estados de los módulos
  const [variants, setVariants] = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const [newAlternativeCombo, setNewAlternativeCombo] = useState(null);
  const [newVariantEquipmentId, setNewVariantEquipmentId] = useState(null);

  const [orientationModal, setOrientationModal] = useState({ 
    visible: false, variantId: null, executorId: null, file: null 
  });
  const [playerModal, setPlayerModal] = useState({ 
    visible: false, url: null, type: null 
  });

  const muscleOptions = useMemo(
    () => muscles.map((m) => ({ value: m.id, label: m.name })),
    [muscles],
  );

  useEffect(() => {
    const loadExerciseVariantsCatalog = async () => {
      try {
        const items = await listExercisesVariants();
        setExerciseVariantsCatalog(items || []);
      } catch (error) {
        console.error('Error al cargar ejercicios:', error);
      }
    };

    const loadExerciseTypes = async () => {
      try {
        const types = await listExerciseTypes();
        setExerciseTypes(types || []);
      } catch (error) {
        console.error('Error al cargar tipos:', error);
      }
    };

    const loadCatalogsData = async () => {
      const catalogSetters = {
        muscles: setMuscles,
        equipment: setEquipment,
        training_styles: setTrainingStyles,
        executors: setExecutors,
      };

      for (const cat of Object.keys(catalogSetters)) {
        try {
          const catalog = await listCatalog(cat);
          catalogSetters[cat](catalog || []);
        } catch (error) {
          console.error(`Error al cargar catálogo ${cat}:`, error);
        }
      }
    };

    loadExerciseVariantsCatalog();
    loadExerciseTypes();
    loadCatalogsData();
  }, [isEdit, id]);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    Promise.all([
      getExerciseById(id),
      getExerciseAlternatives(id).catch(() => [])
    ])
      .then(([exercise, alternativesList]) => {
        form.setFieldsValue({
          title: exercise.title || exercise.internal_title || exercise.name,
          is_unilateral: exercise.is_unilateral || false,
          primary_muscle_ids: exercise.primary_muscles?.map(ts => ts.id) || [],
          secondary_muscle_ids: exercise.secondary_muscles?.map(ts => ts.id) || [],
          training_style_ids: exercise.training_styles?.map(ts => ts.id) || [],
          exercise_type_ids: exercise.types?.map((type) => type.id) || [],
        });
        if (exercise.variants && exercise.variants.length > 0) {
          const loadedVariants = exercise.variants.map(v => {
            const executorsMap = {};
            
            if (v.executors && v.executors.length > 0) {
              v.executors.forEach(ex => {
                
                executorsMap[ex.executor_id] = {
                  vertical_video: ex.vertical_video_url ? { 
                    name: ex.vertical_video_url.split('/').pop(), 
                    url: resolveMedia(ex.vertical_video_url), 
                    isExisting: true 
                  } : null,
                  horizontal_video: ex.horizontal_video_url ? { 
                    name: ex.horizontal_video_url.split('/').pop(), 
                    url: resolveMedia(ex.horizontal_video_url), 
                    isExisting: true 
                  } : null,
                  audio: ex.audio_url ? { 
                    name: ex.audio_url.split('/').pop(), 
                    url: resolveMedia(ex.audio_url), 
                    isExisting: true 
                  } : null,
                };
              });
            }

            return {
              id: v.id,
              equipment_id: v.equipment_id,
              instructions: v.instructions || '',
              executors: executorsMap
            };
          });
          setVariants(loadedVariants);
        }

        const altsSource = exercise.alternatives?.length > 0 ? exercise.alternatives : alternativesList;
        if (altsSource && altsSource.length > 0) {
          const loadedAlternatives = altsSource.map(alt => {
            const equipmentId = alt.equipment_id || null; 
            return {
              id: alt.id,
              comboId: `${alt.alternative_id}-${equipmentId}`,
              exercise_id: alt.alternative_id,
              equipment_id: equipmentId,
              label: `${alt.alternative?.title}  ${equipmentId ? ('-' + (alt?.equipment?.name || alt?.equipment?.title)) : ''}` || 'Alternativa cargada',
            };
          });
          setAlternatives(loadedAlternatives);
        }
      })
      .catch(() => message.error('No se pudo cargar el ejercicio'))
      .finally(() => setLoading(false));
  }, [id, isEdit, form, message]);

  const validateTitle = useCallback(async (_, value) => {
    if (!value || !value.trim()) return Promise.resolve();
    const alphanumericRegex = /^[A-Za-z0-9ÁÉÍÓÚÜÑáéíóúüñ\s]+$/;
    if (!alphanumericRegex.test(value.trim())) {
      return Promise.reject(new Error('El título solo puede contener letras, números y espacios'));
    }
    return Promise.resolve();
  }, []);

  // ---------------------------------------------
  // MÓDULO: VARIANTES Y ARCHIVOS MULTIMEDIA
  // ---------------------------------------------
  const availableEquipmentForVariants = useMemo(() => {
    const usedIds = variants.map(v => v.equipment_id);
    return equipment.filter(eq => !usedIds.includes(eq.id));
  }, [equipment, variants]);

  const getVariantStatus = (variant) => {
    const hasCompleteExecutor = executors.some(ex => {
      const execData = variant.executors?.[ex.id];
      const hasAnyVideo = execData?.vertical_video || execData?.horizontal_video;
      const hasAudio = execData?.audio;
      return hasAnyVideo && hasAudio;
    });
    return hasCompleteExecutor ? 'Habilitado' : 'Pendiente';
  };

  const handleAddVariant = () => {
    if (!newVariantEquipmentId) return;
    setVariants([...variants, { 
      id: `temp-var-${Date.now()}`, 
      equipment_id: newVariantEquipmentId, 
      instructions: '', 
      executors: {} 
    }]);
    setNewVariantEquipmentId(null);
  };

  const handleRemoveVariant = (variantId) => {
    setVariants(variants.filter(v => v.id !== variantId));
  };

  const handleVariantInstructionChange = (variantId, text) => {
    setVariants(prev => prev.map(v => v.id === variantId ? { ...v, instructions: text } : v));
  };

  // Función genérica para asignar un archivo a un campo específico del ejecutor
  const assignMediaToState = (variantId, executorId, field, file) => {
    setVariants(prev => prev.map(v => {
      if (v.id !== variantId) return v;
      return {
        ...v,
        executors: {
          ...v.executors,
          [executorId]: {
            ...v.executors?.[executorId],
            [field]: file
          }
        }
      };
    }));
  };

  // Manejo de la subida de Video (Control de Orientación)
  const handleVideoUpload = (variantId, executorId, file) => {
    const variant = variants.find(v => v.id === variantId);
    const execData = variant?.executors?.[executorId] || {};
    
    const hasVertical = !!execData.vertical_video;
    const hasHorizontal = !!execData.horizontal_video;

    if (!hasVertical && !hasHorizontal) {
      // 0 videos: Preguntar orientación
      setOrientationModal({ visible: true, variantId, executorId, file });
    } else if (hasVertical && !hasHorizontal) {
      // 1 video vertical existente: Asignar a horizontal automáticamente
      assignMediaToState(variantId, executorId, 'horizontal_video', file);
    } else if (!hasVertical && hasHorizontal) {
      // 1 video horizontal existente: Asignar a vertical automáticamente
      assignMediaToState(variantId, executorId, 'vertical_video', file);
    } else {
      // Ya tiene ambos
      message.warning('Ya has subido los dos videos permitidos (Vertical y Horizontal) para este ejecutor.');
    }
    return false; // Prevenir subida automática de Ant Design
  };

  // Manejo de la subida de Audio
  const handleAudioUpload = (variantId, executorId, file) => {
    assignMediaToState(variantId, executorId, 'audio', file);
    return false;
  };

  const handleRemoveMedia = (variantId, executorId, field) => {
    assignMediaToState(variantId, executorId, field, null);
  };

  const openPlayer = (file, type) => {
    const url = file.isExisting ? file.url : URL.createObjectURL(file);
    setPlayerModal({ visible: true, url, type });
  };

  // ---------------------------------------------
  // MÓDULO: ALTERNATIVAS MANUALES
  // ---------------------------------------------
  const alternativeCombinations = useMemo(() => {
    return exerciseVariantsCatalog
      .filter(variant => !(isEdit && variant.exercise_id === Number(id)))
      .filter(variant => !alternatives.some(alt => alt.comboId === `${variant.exercise_id}-${variant.equipment_id}`))
      .map(variant => ({
        value: `${variant.exercise_id}-${variant.equipment_id}`,
        label: variant.displayLabel,
        exercise_id: variant.exercise_id,
        equipment_id: variant.equipment_id,
      }));
  }, [exerciseVariantsCatalog, alternatives, isEdit, id]);

  const handleAddAlternative = () => {
    if (!newAlternativeCombo) return;
    const option = alternativeCombinations.find(opt => opt.value === newAlternativeCombo);
    if (option) {
      setAlternatives([...alternatives, {
        id: `temp-alt-${Date.now()}`,
        comboId: option.value,
        exercise_id: option.exercise_id,
        equipment_id: option.equipment_id,
        label: option.label
      }]);
      setNewAlternativeCombo(null);
    }
  };

  const handleRemoveAlternative = (comboIdToRemove) => {
    setAlternatives(alternatives.filter(alt => alt.comboId !== comboIdToRemove));
  };

  // ---------------------------------------------
  // GUARDAR EJERCICIO
  // ---------------------------------------------
  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const exists = await checkTitleExists(values.title.trim(), isEdit ? Number(id) : null);
      if (exists) {
        message.error('Este título ya existe.');
        return;
      }
      const alternativesPayload = alternatives.map((alt, index) => ({
        alternative_id: alt.exercise_id,
        equipment_id: alt.equipment_id,
        display_order: index,
        ...(alt.id && !alt.id.toString().startsWith('temp-') && { id: Number(alt.id) })
      }));

      // Procesar variantes con soporte para video vertical y horizontal
      const variantsPayload = await Promise.all(
        variants.map(async (variant) => {
          const executorsPayload = await Promise.all(
            executors.map(async (exec) => {
              const media = variant.executors?.[exec.id] || {};
              
              let verticalVideoUrl = null;
              let horizontalVideoUrl = null;
              let audioUrl = null;

              if (media.vertical_video) {
                if (media.vertical_video.isExisting) {
                  verticalVideoUrl = media.vertical_video?.url?.replace('https://', '');
                } else {
                  const videoRes = await uploadExerciseMedia(media.vertical_video);
                  verticalVideoUrl = videoRes.url;
                }
              }
              
              if (media.horizontal_video) {
                if (media.horizontal_video.isExisting) {
                  horizontalVideoUrl = media.horizontal_video?.url?.replace('https://', '');
                } else {
                  const videoRes = await uploadExerciseMedia(media.horizontal_video);
                  horizontalVideoUrl = videoRes.url;
                }
              }

              if (media.audio) {
                if (media.audio.isExisting) {
                  audioUrl = media.audio?.url?.replace('https://', '');
                } else {
                  const audioRes = await uploadExerciseAudio(media.audio);
                  audioUrl = audioRes.url;
                }
              }

              return {
                executor_id: exec.id,
                vertical_video_url: verticalVideoUrl,
                horizontal_video_url: horizontalVideoUrl, 
                audio_url: audioUrl
              };
            })
          );

          const uiStatus = getVariantStatus(variant);
          const dbStatus = uiStatus === 'Habilitado' ? 'enabled' : 'pending';
          return {
            equipment_id: variant.equipment_id,
            instructions: variant.instructions || '',
            status: dbStatus,
            executors: executorsPayload
          };
        })
      );

      const payload = {
        title: values.title.trim(),
        description: values.title.trim(),
        is_unilateral: values.is_unilateral || false,
        primary_muscle_ids: values.primary_muscle_ids || [],
        secondary_muscle_ids: values.secondary_muscle_ids || [],
        training_style_ids: values.training_style_ids || [],
        exercise_type_ids: values.exercise_type_ids || [],
        alternatives: alternativesPayload,
        variants: variantsPayload,
      };

      if (isEdit && id) {
        await updateExercise(id, payload);
        message.success('Ejercicio actualizado correctamente');
      } else {
        await createExercise(payload);
        message.success('Ejercicio creado correctamente');
      }

      navigate('/exercises/all');
    } catch (err) {
      console.error(err);
      message.error('Error al guardar el ejercicio');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------
  // COMPONENTES AUXILIARES PARA UI
  // ---------------------------------------------
  const MediaPreviewRow = ({ file, label, type, onRemove }) => {
    if (!file) return null;
    return (
      <div 
        style={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
          padding: '8px 12px', background: '#f5f5f5', borderRadius: 6, marginBottom: 8, cursor: 'pointer' 
        }}
        onClick={() => openPlayer(file, type)}
      >
        <Space>
          <PlayCircleOutlined style={{ color: '#1890ff' }} />
          <Badge color={type === 'video' ? 'blue' : 'green'} text={<span style={{fontSize: 12}}>{label}</span>} />
          <Text ellipsis style={{ maxWidth: 180, fontSize: 13, margin: 0 }}>{file.name}</Text>
        </Space>
        <Button 
          type="text" danger icon={<DeleteOutlined />} size="small"
          onClick={(e) => { e.stopPropagation(); onRemove(); }} 
        />
      </div>
    );
  };

  return (
    <div style={{ padding: '0 24px 24px 24px', backgroundColor: '#fafafa', minHeight: '100vh' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 0' }}>
        <div>
          <Text type="secondary" style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => navigate(-1)}>
            ← Catálogo de Ejercicios
          </Text>
          <Title level={2} style={{ margin: 0 }}>{isEdit ? 'Editar ejercicio' : 'Nuevo ejercicio'}</Title>
        </div>
        <Space>
          <Button onClick={() => navigate(-1)} disabled={loading} style={{ border: 'none', background: 'transparent' }}>
            Cancelar
          </Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading} style={{ borderRadius: 8 }}>
            Guardar ejercicio
          </Button>
        </Space>
      </div>

      <Form form={form} layout="vertical" onFinish={onSubmit} autoComplete="off">
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            
            {/* 1. DATOS BASE */}
            <Card style={{ borderRadius: 12, marginBottom: 24 }} bordered={false}>
              <Title level={4}>Datos base</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Nombre del ejercicio" name="title" rules={[{ required: true, message: 'Requerido' }, { validator: validateTitle }]}>
                    <Input placeholder="Ej. Sentadilla goblet" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="¿Es unilateral?" name="is_unilateral" valuePropName="checked">
                      <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Músculo primario" name="primary_muscle_ids">
                    <Select size="large" placeholder="Ej. Cuádriceps" mode='multiple' allowClear options={muscleOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Músculo secundario" name="secondary_muscle_ids">
                    <Select size="large" placeholder="Ej. Glúteo, Core" mode='multiple' allowClear options={muscleOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Estilo de entrenamiento" name="training_style_ids">
                    <Select size="large" placeholder="Seleccione estilo" mode="multiple" allowClear options={trainingStyles.map(t => ({ value: t.id, label: t.name }))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Tipo de ejercicio" name="exercise_type_ids">
                    <Select size="large" placeholder="Seleccione tipo" mode="multiple" allowClear options={exerciseTypes.map(t => ({ value: t.id, label: t.name }))} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* 2. VARIANTES DE EQUIPO */}
            <Card style={{ borderRadius: 12, marginBottom: 24 }} bordered={false}>
              <Title level={4}>Variantes de equipo</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                Sube un máximo de dos videos (Horizontal y Vertical) y un audio por cada ejecutor.
              </Text>

              <Space style={{ marginBottom: 16 }} className="w-full">
                <Select
                  style={{ width: 250 }}
                  placeholder="Selecciona un equipo"
                  value={newVariantEquipmentId}
                  onChange={setNewVariantEquipmentId}
                  options={availableEquipmentForVariants.map(eq => ({ value: eq.id, label: eq.name }))}
                />
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddVariant} disabled={!newVariantEquipmentId}>
                  Agregar variante
                </Button>
              </Space>

              {variants.length > 0 && (
                <Collapse accordion style={{ background: '#fff', borderRadius: 8 }}>
                  {variants.map(variant => {
                    const equipmentName = equipment.find(e => e.id === variant.equipment_id)?.name || 'Equipo';
                    const status = getVariantStatus(variant);
                    
                    return (
                      <Panel 
                        key={variant.id} 
                        header={<b>{equipmentName}</b>}
                        extra={<Badge color={status === 'Habilitado' ? 'green' : 'orange'} text={<span style={{ color: status === 'Habilitado' ? '#52c41a' : '#faad14', fontWeight: 500 }}>{status}</span>} />}
                      >
                        <Form.Item label="Instrucciones de ejecución">
                          <Input.TextArea rows={3} value={variant.instructions} onChange={(e) => handleVariantInstructionChange(variant.id, e.target.value)} />
                        </Form.Item>
                        
                        <Text strong style={{ fontSize: 12, color: '#888' }}>MULTIMEDIA POR EJECUTOR</Text>
                        <Divider style={{ margin: '12px 0' }} />

                        {executors.map(exec => {
                          const execData = variant.executors?.[exec.id] || {};
                          return (
                            <Row key={exec.id} style={{ marginBottom: 24 }}>
                              <Col span={24}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                  <Text strong>{exec.name}</Text>
                                  <Space>
                                    <Upload beforeUpload={(f) => handleVideoUpload(variant.id, exec.id, f)} showUploadList={false} accept="video/*">
                                      <Button size="small" icon={<UploadOutlined />}>Añadir Video</Button>
                                    </Upload>
                                    <Upload beforeUpload={(f) => handleAudioUpload(variant.id, exec.id, f)} showUploadList={false} accept="audio/*">
                                      <Button size="small" icon={<UploadOutlined />}>Añadir Audio</Button>
                                    </Upload>
                                  </Space>
                                </div>

                                {/* PREVISUALIZACIÓN COMPACTA */}
                                <div>
                                  <MediaPreviewRow file={execData.vertical_video} label="Vertical" type="video" onRemove={() => handleRemoveMedia(variant.id, exec.id, 'vertical_video')} />
                                  <MediaPreviewRow file={execData.horizontal_video} label="Horizontal" type="video" onRemove={() => handleRemoveMedia(variant.id, exec.id, 'horizontal_video')} />
                                  <MediaPreviewRow file={execData.audio} label="Audio" type="audio" onRemove={() => handleRemoveMedia(variant.id, exec.id, 'audio')} />
                                  
                                  {!execData.vertical_video && !execData.horizontal_video && !execData.audio && (
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '4px 12px', background: '#fafafa', border: '1px dashed #d9d9d9', borderRadius: 6 }}>
                                      Sin multimedia cargada para {exec.name}
                                    </Text>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          );
                        })}
                        
                        <div style={{ marginTop: 24 }}>
                          <Button type="text" danger onClick={() => handleRemoveVariant(variant.id)}>
                            Eliminar esta variante
                          </Button>
                        </div>
                      </Panel>
                    );
                  })}
                </Collapse>
              )}
            </Card>

            {/* 3. ALTERNATIVAS */}
            <Card style={{ borderRadius: 12 }} bordered={false}>
              <Title level={4}>Alternativas</Title>
              <div style={{ marginBottom: 24 }}>
                <Text strong type="secondary" style={{ fontSize: 12 }}>MANUALES</Text>
              </div>

              {alternatives.map((alt) => (
                <div key={alt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Text>{alt.label}</Text>
                  <Button type="text" danger onClick={() => handleRemoveAlternative(alt.comboId)}>Quitar</Button>
                </div>
              ))}

              <Space style={{ width: '100%' }}>
                <Select
                  showSearch style={{ width: 400 }} size="large" placeholder="Seleccione un ejercicio — Equipo"
                  value={newAlternativeCombo} onChange={setNewAlternativeCombo}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={alternativeCombinations}
                />
                <Button size="large" onClick={handleAddAlternative} disabled={!newAlternativeCombo}>+ Agregar</Button>
              </Space>
            </Card>
          </Col>

          {/* COLUMNA DERECHA */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #f0f0f0' }}>
              <Title level={4}>¿Dónde se usa?</Title>
              <Text>Este ejercicio (o alguna de sus variantes) aún no está asignado a ningún plan.</Text>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* MODAL DE ORIENTACIÓN (Primer Video) */}
      <Modal
        title="Orientación del video"
        open={orientationModal.visible}
        footer={null}
        closable={false}
        centered
        width={350}
      >
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Text>¿Cuál es la orientación del primer video que estás subiendo?</Text>
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button 
            block size="large" type="primary"
            onClick={() => {
              assignMediaToState(orientationModal.variantId, orientationModal.executorId, 'horizontal_video', orientationModal.file);
              setOrientationModal({ visible: false, variantId: null, executorId: null, file: null });
            }}
          >
            Horizontal
          </Button>
          <Button 
            block size="large"
            onClick={() => {
              assignMediaToState(orientationModal.variantId, orientationModal.executorId, 'vertical_video', orientationModal.file);
              setOrientationModal({ visible: false, variantId: null, executorId: null, file: null });
            }}
          >
            Vertical
          </Button>
        </Space>
      </Modal>

      {/* MODAL DEL REPRODUCTOR */}
      <Modal
        open={playerModal.visible}
        title="Reproductor de Medios"
        footer={null}
        destroyOnClose // Fundamental para que el video/audio se detenga al cerrar
        onCancel={() => setPlayerModal({ visible: false, url: null, type: null })}
        centered
        bodyStyle={{ padding: 0 }}
      >
        {playerModal.type === 'video' ? (
          <video src={playerModal.url} controls autoPlay style={{ width: '100%', maxHeight: '70vh', outline: 'none', display: 'block', backgroundColor: '#000' }} />
        ) : (
          <div style={{ padding: '24px', background: '#f5f5f5', textAlign: 'center' }}>
            <audio src={playerModal.url} controls autoPlay style={{ width: '100%', outline: 'none' }} />
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ExerciseUpsertPage;